// A script cell: the Python a composite's run receipt or provenance names,
// read only (re-running a script needs the chat's sandbox, which is not in
// the first version). The loader comes from the page.
import React, { useEffect, useState } from 'react'
import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import { HAIRLINE, INNER_RADIUS } from '../../../agent/components/surface'
import { appRelativeScriptPath, type TextLoader } from '../../composite-table/ProvenanceModal'

export interface NotebookScriptCellProps {
  path: string
  loadScript: TextLoader
}

export const NotebookScriptCell: React.FC<NotebookScriptCellProps> = ({ path, loadScript }) => {
  const shipped = appRelativeScriptPath(path)
  const [source, setSource] = useState<string | null>(null)
  const [error, setError] = useState('')

  // Read the file when the cell shows a new path.
  useEffect(() => {
    if (!shipped) return
    let cancelled = false
    setSource(null)
    setError('')
    loadScript(shipped)
      .then((text) => { if (!cancelled) setSource(text) })
      .catch((e: Error) => { if (!cancelled) setError(e?.message || String(e)) })
    return () => { cancelled = true }
  }, [shipped])

  if (!shipped) {
    return (
      <Typography variant="body2" color="text.secondary">
        This script is not in the chat’s data folder, so it cannot be shown.
      </Typography>
    )
  }
  if (error) return <Alert severity="warning">Could not read {path}: {error}</Alert>
  if (source === null) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
  return (
    <Box
      component="pre"
      data-testid="notebook-script-source"
      tabIndex={0}
      aria-label={`Source of ${path}`}
      sx={{
        m: 0, p: 1.5, maxHeight: 360, overflow: 'auto', border: HAIRLINE, borderRadius: INNER_RADIUS,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, whiteSpace: 'pre',
      }}
    >
      {source}
    </Box>
  )
}

export default NotebookScriptCell
